import { Auth0ContextInterface, User as Auth0User } from '@auth0/auth0-react';
import { useEffect } from 'react';

import { loggedIn } from '@/redux/slices/loggedUserSlice';
import { useAppDispatch } from '@/redux/store';
import { withTimeout } from '@/helpers/withTimeout';

const ACCESS_TOKEN_TIMEOUT_MS = 10000;

type StoreLoggedUserProps = {
  auth0UserAuthenticated: boolean;
  getAccessTokenSilently: Auth0ContextInterface['getAccessTokenSilently'];
  auth0User?: Auth0User;
  loginWithRedirect: Auth0ContextInterface['loginWithRedirect'];
};

export const useStoreLoggedUser = ({
  auth0UserAuthenticated,
  getAccessTokenSilently,
  auth0User,
  loginWithRedirect,
}: StoreLoggedUserProps) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const saveLoggedUser = async () => {
      if (auth0UserAuthenticated) {
        try {
          const accessToken = await withTimeout(
            getAccessTokenSilently(),
            ACCESS_TOKEN_TIMEOUT_MS,
            'Timed out waiting for getAccessTokenSilently'
          );
          dispatch(
            loggedIn({
              auth0User, // auth0User contains the JWT decoded data
              accessToken,
            })
          );
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
          console.error('Failed to silently retrieve access token', e);
          window?.DD_RUM?.addError(e, {
            location: 'getAccessTokenSilently',
          });
          // Any failure here (expired/missing refresh token, or a hung/blocked
          // silent auth check) leaves the user stuck with no session — send them
          // through a full redirect login instead of leaving the app in limbo.
          loginWithRedirect({
            appState: {
              returnTo: `${window.location.pathname}${window.location.search}`,
            },
          });
        }
      }
    };

    saveLoggedUser();
  }, [
    auth0UserAuthenticated,
    auth0User,
    dispatch,
    getAccessTokenSilently,
    loginWithRedirect,
  ]);
};
